import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { createUUID } from "@miplatform/utils";

export const useAccountDrawsTabViewModel = ({
  getAccountDetails,
  stateManager,
  setEditMode,
  isRemoved,
  getValues,
  setValue,
  append,
  remove,
  reset,
}) => {
  const [draws, setDraws] = useState([]);
  const GET_DRAWS_UUID = createUUID();
  const HANDLE_SAVE_DRAWS_UUID = createUUID();

  const handleSetDraws = (draws) => {
    setDraws(draws);
    const drawTotal = handleCalculateTotal(draws);
    reset({ draws, drawTotal });
  };

  const getDraws = async (accountId) => {
    stateManager.abort(GET_DRAWS_UUID);
    return stateManager.run(async () => {
      const response = await homeService.draws.getAll({
        accountId,
        sort: "displayOrder asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        handleSetDraws(response.data);
      }
    }, GET_DRAWS_UUID);
  };

  const handleSaveDraws = async (data, accountId) => {
    const filteredDraws = data
      .filter((draw) => !draw.removed)
      .map((draw, index) => {
        return {
          ...draw,
          displayOrder: index,
        };
      });
    stateManager.abort(HANDLE_SAVE_DRAWS_UUID);
    return stateManager.run(async () => {
      const response = await homeService.draws.update({
        accountId,
        data: filteredDraws,
        toastMessageConfig: {
          success: { text: "Successfully saved draws." },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        // fetch account details to update the chip label notification on tab
        getAccountDetails(accountId);
        handleSetDraws(response.data);
        setEditMode(false);
        handleCalculateTotal(getValues("draws"));
      }
    }, HANDLE_SAVE_DRAWS_UUID);
  };

  const handleEnableEdit = () => {
    setEditMode(true);
    if (draws.length === 0) {
      append({ name: "", displayOrder: 0 });
    }
  };

  const handleAddDraw = () => {
    append({ name: "", displayOrder: getValues("draws").length });
  };

  const handleRemoveDraw = (index) => {
    const isDrawRemoved = isRemoved(index);
    const isDrawNew = draws.every((draw) => {
      return draw.id !== getValues(`draws[${index}].id`);
    });

    if (isDrawNew) {
      remove(index);
      handleCalculateTotal(getValues("draws"));
      return;
    }

    if (isDrawRemoved) {
      setValue(`draws[${index}].removed`, false, {
        shouldValidate: true,
      });
    } else {
      setValue(`draws[${index}].removed`, true, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    handleCalculateTotal(getValues("draws"));
  };

  const handleEvenDistribution = () => {
    const currentDraws = getValues("draws");
    const activeDraws = currentDraws.filter((_, index) => !isRemoved(index));
    const updatedDrawsDistribution = currentDraws.map((draw, index) => {
      if (isRemoved(index)) {
        return draw;
      }
      return {
        ...draw,
        percentage: parseFloat((100 / activeDraws.length).toFixed(2)),
      };
    });
    setValue("draws", updatedDrawsDistribution, {
      shouldValidate: true,
      shouldDirty: true,
    });
    handleCalculateTotal(updatedDrawsDistribution);
  };

  const handleCalculateTotal = (updatedDraws) => {
    const activeDraws = updatedDraws.filter((_, index) => !isRemoved(index));
    const total = parseFloat(
      activeDraws
        .reduce((acc, d) => acc + (d.percentage ? d.percentage : 0), 0)
        .toFixed(2)
    );
    setValue("drawTotal", total, { shouldValidate: true, shouldDirty: true });
    return total;
  };

  return {
    draws,
    setDraws,
    getDraws,
    handleSaveDraws,
    handleAddDraw,
    handleRemoveDraw,
    handleEnableEdit,
    isLoading: stateManager.isBusy(),
    handleEvenDistribution,
    handleCalculateTotal,
  };
};
